/**
 * CarouselLayouts.jsx
 *
 * Per-template Slide renderers that visually approximate the 6 custom
 * ReportLab PDF layouts produced by `carousel_service.py`. Each renderer
 * accepts the same props from CarouselDialog and outputs an HTML/CSS
 * approximation that lets users *see* what they'll get before downloading.
 *
 * Layout reference (must stay in sync with backend):
 *   - editorial_bold     -> EditorialBoldSlide
 *   - index_card         -> IndexCardSlide
 *   - portrait_spotlight -> PortraitSpotlightSlide
 *   - pop_frame          -> PopFrameSlide
 *   - magazine_mono      -> MagazineMonoSlide
 *   - quote_stack        -> QuoteStackSlide
 *
 * All other templates fall through to the existing GenericSlide in
 * CarouselDialog.jsx (they share the same base layout in the PDF).
 */

import React from 'react';

export const CUSTOM_LAYOUT_KEYS = [
  'editorial_bold',
  'index_card',
  'portrait_spotlight',
  'pop_frame',
  'magazine_mono',
  'quote_stack',
];

export function isCustomLayout(templateKey) {
  return CUSTOM_LAYOUT_KEYS.includes(templateKey);
}

// --- Common helpers ---------------------------------------------------------
const SERIF = '"Playfair Display", Georgia, serif';
const SANS = "Inter, -apple-system, system-ui, sans-serif";
const MONO = '"Source Code Pro", ui-monospace, monospace';

function fmtPage(idx, total) {
  return `${String(idx + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
}

function pickAccentWords(title) {
  if (!title) return new Set();
  // Quoted text first
  const quoted = [];
  const re = /"([^"]+)"|\(([^)]+)\)/g;
  let m;
  while ((m = re.exec(title)) !== null) {
    const grp = m[1] || m[2] || '';
    grp.split(/\s+/).forEach((w) => {
      const c = w.replace(/[.,:;\u2014\u2013-\u2019'?!"]+/g, '').toLowerCase();
      if (c) quoted.push(c);
    });
  }
  if (quoted.length) return new Set(quoted);
  // Else last word
  const parts = title.trim().split(/\s+/);
  if (!parts.length) return new Set();
  const last = parts[parts.length - 1]
    .replace(/[.,:;\u2014\u2013-\u2019'?!"]+/g, '')
    .toLowerCase();
  return new Set(last ? [last] : []);
}

function renderAccentedTitle(title, accentColor, titleColor) {
  if (!title) return null;
  const accents = pickAccentWords(title);
  return title.split(/(\s+)/).map((word, i) => {
    if (/^\s+$/.test(word)) return word;
    const clean = word.replace(/[.,:;\u2014\u2013-\u2019'?!"]+/g, '').toLowerCase();
    const isAccent = accents.has(clean);
    return (
      <span key={i} style={{ color: isAccent ? accentColor : titleColor }}>
        {word}
      </span>
    );
  });
}

/**
 * Standard base wrapper used by all 6 layouts. Renders a 4:5 aspect-ratio
 * card with the given background, plus a slot for absolutely-positioned
 * decorations. The caller (CarouselDialog) overlays a draggable logo on top.
 */
function LayoutFrame({ children, bg, testId, fontFamily }) {
  return (
    <div
      data-testid={testId}
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden border border-border select-none"
      style={{ backgroundColor: bg, fontFamily }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Editorial Bold
// ---------------------------------------------------------------------------
export function EditorialBoldSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = slide?.title || '';
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const kicker = (slide?.kicker || fmtPage(idx, total)).toUpperCase();
  const img = slide?.image_data_url;
  const showImage = img && (slide?.image_mode || 'none').toLowerCase() === 'center-rect';
  const isCover = role === 'cover';

  // Dot matrix (8x8) ornament top-right
  const dots = [];
  for (let r = 0; r < 8; r++) {
    for (let col = 0; col < 8; col++) {
      dots.push(
        <span
          key={`${r}-${col}`}
          className="absolute rounded-full"
          style={{
            width: 4,
            height: 4,
            right: `${5 + col * 3.2}%`,
            top: `${7 + r * 3.0}%`,
            backgroundColor: palette.accent,
            opacity: 0.14,
          }}
        />,
      );
    }
  }

  return (
    <LayoutFrame bg={palette.bg} testId={testId} fontFamily={SERIF}>
      {/* Dot matrix decoration */}
      <div className="absolute inset-0 pointer-events-none">{dots}</div>

      {/* Optional image card right */}
      {showImage && (
        <div
          className="absolute rounded-md overflow-hidden border border-black/10 shadow-lg"
          style={{ right: '8%', top: '34%', width: '32%', aspectRatio: '3/4' }}
        >
          <img src={img} alt="" className="w-full h-full object-cover" draggable={false} />
        </div>
      )}

      {/* Kicker */}
      <div
        className="absolute"
        style={{
          top: '8.5%',
          left: '7.5%',
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '0.16em',
          color: palette.kicker,
        }}
      >
        {kicker}
      </div>

      {/* Title (serif, big, with accent word) */}
      <div
        className="absolute font-extrabold"
        style={{
          top: '17%',
          left: '7.5%',
          right: showImage ? '45%' : '8%',
          fontSize: isCover ? '34px' : '28px',
          lineHeight: 1.04,
          color: palette.title,
          fontFamily: SERIF,
        }}
      >
        {renderAccentedTitle(title, palette.accent, palette.title)}
      </div>

      {/* Body */}
      {body && !isCover && (
        <div
          className="absolute"
          style={{
            top: '63%',
            left: '7.5%',
            right: showImage ? '45%' : '8%',
            fontSize: '11px',
            lineHeight: 1.5,
            color: palette.body,
            fontFamily: SANS,
          }}
        >
          {body.slice(0, 220)}
        </div>
      )}

      {/* Footer */}
      <div
        className="absolute left-[7.5%] right-[7.5%] flex items-center justify-between"
        style={{
          bottom: '5%',
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '0.04em',
          color: palette.body,
        }}
      >
        <span style={{ textTransform: 'lowercase' }}>{authorName}</span>
        <span style={{ color: palette.kicker }}>{(brandLabel || '').toUpperCase()}</span>
        <span>{fmtPage(idx, total)}</span>
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// 2) Index Card
// ---------------------------------------------------------------------------
export function IndexCardSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = slide?.title || '';
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const img = slide?.image_data_url;
  const imageMode = (slide?.image_mode || 'full').toLowerCase();
  const hasFullImg = img && imageMode === 'full';
  const hasBandImg = img && imageMode === 'vertical-band';

  // Card position: alternate corner — even idx -> bottom-left, odd -> top-right
  // For vertical-band layout, card is fixed to right half
  let cardStyle;
  if (hasBandImg) {
    cardStyle = { left: '54%', right: '7%', top: '17%', bottom: '17%' };
  } else if (idx % 2 === 0) {
    cardStyle = { left: '7%', right: '20%', top: '10%', bottom: '10%' };
  } else {
    cardStyle = { left: '20%', right: '7%', top: '10%', bottom: '10%' };
  }

  return (
    <LayoutFrame
      bg={hasFullImg ? '#1F2937' : palette.bg}
      testId={testId}
      fontFamily={SANS}
    >
      {/* Full-bleed image background */}
      {hasFullImg && (
        <img
          src={img}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      )}

      {/* Vertical-band image left half */}
      {hasBandImg && (
        <img
          src={img}
          alt=""
          className="absolute left-0 top-0 h-full object-cover"
          style={{ width: '50%' }}
          draggable={false}
        />
      )}

      {/* Top brand stripe (royal blue accent, ~1.2% height) */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{ height: '1.5%', backgroundColor: palette.accent }}
      />

      {/* Floating white text card */}
      <div
        className="absolute bg-white rounded-md border border-gray-200 shadow-xl flex flex-col"
        style={{
          ...cardStyle,
          padding: '14px 18px',
        }}
      >
        {/* Numeral */}
        <div className="flex items-baseline gap-1.5">
          <span
            className="font-extrabold leading-none"
            style={{
              color: palette.accent,
              fontSize: '52px',
              fontFamily: SERIF,
            }}
          >
            {String(idx + 1).padStart(2, '0')}
          </span>
          <span className="text-gray-400" style={{ fontSize: '10px', fontFamily: SANS }}>
            of {String(total).padStart(2, '0')}
          </span>
        </div>

        {/* Title */}
        <p
          className="mt-3 font-bold text-gray-900"
          style={{
            fontSize: role === 'cover' ? '17px' : '14px',
            lineHeight: 1.2,
            fontFamily: SANS,
          }}
        >
          {title}
        </p>

        {/* Body */}
        {body && (
          <p
            className="mt-2 text-gray-700"
            style={{ fontSize: '10px', lineHeight: 1.45, fontFamily: SANS }}
          >
            {body.slice(0, 180)}
          </p>
        )}

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span
            style={{
              fontSize: '8px',
              fontFamily: MONO,
              color: palette.accent,
              letterSpacing: '0.1em',
            }}
          >
            {(brandLabel || '').toUpperCase()}
          </span>
          <span style={{ fontSize: '8px', color: '#9CA3AF', fontFamily: MONO }}>
            {(authorName || '').toLowerCase()}
          </span>
        </div>
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// 3) Portrait Spotlight
// ---------------------------------------------------------------------------
export function PortraitSpotlightSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = slide?.title || '';
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const kicker = (slide?.kicker || fmtPage(idx, total)).toUpperCase();
  const img = slide?.image_data_url;
  const imageMode = (slide?.image_mode || 'vertical-band').toLowerCase();
  const showVerticalBand = !img || imageMode === 'vertical-band';
  const showCenterRect = img && imageMode === 'center-rect';

  return (
    <LayoutFrame bg={palette.bg} testId={testId} fontFamily={SANS}>
      {/* Right half: vertical image band (or solid accent if no image) */}
      {showVerticalBand && (
        <div
          className="absolute top-0 right-0 h-full"
          style={{
            width: '50%',
            backgroundColor: img ? 'transparent' : palette.accent,
          }}
        >
          {img && (
            <>
              <img
                src={img}
                alt=""
                className="w-full h-full object-cover"
                draggable={false}
              />
              {/* Tint overlay */}
              <div className="absolute inset-0" style={{ backgroundColor: palette.bg, opacity: 0.35 }} />
            </>
          )}
        </div>
      )}
      {showCenterRect && (
        <div
          className="absolute rounded-md overflow-hidden"
          style={{
            right: '6%',
            top: '24%',
            width: '38%',
            aspectRatio: '3/4',
            backgroundColor: palette.accent,
            padding: '6px',
          }}
        >
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover rounded-sm"
            draggable={false}
          />
        </div>
      )}

      {/* Top-right circular arrow badge */}
      <div
        className="absolute rounded-full flex items-center justify-center font-bold"
        style={{
          right: '7%',
          top: '6.5%',
          width: '14%',
          aspectRatio: '1/1',
          backgroundColor: palette.accent,
          color: palette.bg,
          fontFamily: SERIF,
          fontSize: '20px',
        }}
        aria-hidden
      >
        ↗
      </div>

      {/* Kicker */}
      <div
        className="absolute"
        style={{
          top: '11%',
          left: '6.5%',
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '0.16em',
          color: palette.kicker,
        }}
      >
        {kicker}
      </div>

      {/* Title - left side only */}
      <div
        className="absolute font-bold"
        style={{
          top: '18%',
          left: '6.5%',
          width: '42%',
          fontSize: role === 'cover' ? '28px' : '24px',
          lineHeight: 1.05,
          color: palette.title,
          fontFamily: SERIF,
        }}
      >
        {title}
      </div>

      {/* Body - left side */}
      {body && (
        <div
          className="absolute"
          style={{
            top: '60%',
            left: '6.5%',
            width: '42%',
            fontSize: '10px',
            lineHeight: 1.45,
            color: palette.body,
            fontFamily: SANS,
          }}
        >
          {body.slice(0, 160)}
        </div>
      )}

      {/* Bottom caption strip */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-[6.5%]"
        style={{
          height: '6.5%',
          backgroundColor: palette.accent,
          color: palette.bg,
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '0.08em',
        }}
      >
        <span>{(authorName || '').toUpperCase()}</span>
        <span>
          {(brandLabel || '').toUpperCase()} · {fmtPage(idx, total)}
        </span>
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// 4) Pop Frame
// ---------------------------------------------------------------------------
export function PopFrameSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = (slide?.title || '').toUpperCase();
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const img = slide?.image_data_url;
  const imageMode = (slide?.image_mode || 'circle').toLowerCase();
  const showCircleImg = img && (imageMode === 'circle' || imageMode === 'center-rect');

  return (
    <LayoutFrame bg={palette.bg} testId={testId} fontFamily={SANS}>
      {/* Concentric circles ornament top-left */}
      <svg
        className="absolute pointer-events-none"
        style={{ left: '-10%', top: '-12%' }}
        width="50%"
        viewBox="0 0 240 240"
        aria-hidden
      >
        {[110, 80, 50, 24].map((r) => (
          <circle
            key={r}
            cx="120"
            cy="120"
            r={r}
            fill="none"
            stroke={palette.accent}
            strokeWidth="1.5"
          />
        ))}
      </svg>

      {/* Title - top, centered, uppercase */}
      <div
        className="absolute left-[8%] right-[8%] text-center font-extrabold"
        style={{
          top: '8%',
          color: palette.title,
          fontSize: role === 'cover' ? '24px' : '20px',
          lineHeight: 1.05,
          fontFamily: SANS,
          letterSpacing: '-0.01em',
        }}
      >
        {title.slice(0, 70)}
      </div>

      {/* Big circular image holder (or accent solid circle) */}
      <div
        className="absolute left-1/2 -translate-x-1/2 rounded-full overflow-hidden"
        style={{
          top: '32%',
          width: '54%',
          aspectRatio: '1/1',
          backgroundColor: palette.accent,
          boxShadow: `0 0 0 6px ${palette.accent}`,
        }}
      >
        {showCircleImg ? (
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full" style={{ backgroundColor: palette.accent }} />
        )}
      </div>

      {/* Body below */}
      {body && (
        <div
          className="absolute left-[10%] right-[10%] text-center"
          style={{
            top: '83%',
            fontSize: '10px',
            lineHeight: 1.4,
            color: palette.body,
            fontFamily: SANS,
            fontWeight: 500,
          }}
        >
          {body.slice(0, 120)}
        </div>
      )}

      {/* Footer */}
      <div
        className="absolute left-[7%] right-[7%] flex items-center justify-between"
        style={{
          bottom: '3.5%',
          fontFamily: MONO,
          fontSize: '9px',
          color: palette.kicker,
          letterSpacing: '0.08em',
        }}
      >
        <span>{(authorName || '').toUpperCase()}</span>
        <span>
          {(brandLabel || '').toUpperCase()} · {fmtPage(idx, total)}
        </span>
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// 5) Magazine Mono
// ---------------------------------------------------------------------------
export function MagazineMonoSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = slide?.title || '';
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const img = slide?.image_data_url;
  const imageMode = (slide?.image_mode || 'none').toLowerCase();
  const hasBand = img && imageMode === 'vertical-band';

  return (
    <LayoutFrame bg={palette.bg} testId={testId} fontFamily={SANS}>
      {/* Top hairline */}
      <div
        className="absolute left-[7%] right-[7%]"
        style={{ top: '7.5%', height: '2px', backgroundColor: palette.title }}
      />
      {/* Bottom hairline */}
      <div
        className="absolute left-[7%] right-[7%]"
        style={{ bottom: '7.5%', height: '2px', backgroundColor: palette.title }}
      />

      {/* Folio: ISSUE + PAGE */}
      <div
        className="absolute left-[7%] right-[7%] flex items-center justify-between"
        style={{
          top: '9%',
          fontFamily: MONO,
          fontSize: '8.5px',
          letterSpacing: '0.18em',
          color: palette.kicker,
        }}
      >
        <span>ISSUE {(brandLabel || 'EXEC').toUpperCase()}</span>
        <span>PAGE {fmtPage(idx, total)}</span>
      </div>

      {/* Optional vertical-band image right */}
      {hasBand && (
        <div
          className="absolute"
          style={{
            right: '7%',
            top: '13%',
            bottom: '14%',
            width: '32%',
            backgroundImage: `url(${img})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Title — huge bold sans, left-aligned */}
      <div
        className="absolute font-extrabold"
        style={{
          top: '16%',
          left: '7%',
          right: hasBand ? '43%' : '7%',
          fontSize: role === 'cover' ? '32px' : '26px',
          lineHeight: 1.05,
          color: palette.title,
          fontFamily: SANS,
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </div>

      {/* Body */}
      {body && (
        <div
          className="absolute"
          style={{
            top: '58%',
            left: '7%',
            right: hasBand ? '43%' : '7%',
            fontSize: '11px',
            lineHeight: 1.5,
            color: palette.body,
            fontFamily: SANS,
          }}
        >
          {body.slice(0, 220)}
        </div>
      )}

      {/* Bottom byline */}
      <div
        className="absolute"
        style={{
          bottom: '4.5%',
          left: '7%',
          fontFamily: MONO,
          fontSize: '9px',
          letterSpacing: '0.16em',
          color: palette.title,
        }}
      >
        BY {(authorName || 'AUTHOR').toUpperCase()}
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// 6) Quote Stack
// ---------------------------------------------------------------------------
export function QuoteStackSlide({ slide, idx, total, palette, brandLabel, authorName, testId }) {
  const title = slide?.title || '';
  const body = slide?.body || '';
  const role = (slide?.role || 'insight').toLowerCase();
  const img = slide?.image_data_url;
  const imageMode = (slide?.image_mode || 'circle').toLowerCase();
  const hasAvatar = img && (imageMode === 'circle' || imageMode === 'center-rect');

  return (
    <LayoutFrame bg={palette.bg} testId={testId} fontFamily={SERIF}>
      {/* Oversized opening quote (decorative) */}
      <span
        className="absolute select-none pointer-events-none font-bold leading-none"
        style={{
          left: '4%',
          top: '-8%',
          fontSize: '180px',
          color: palette.accent,
          fontFamily: SERIF,
        }}
        aria-hidden
      >
        “
      </span>

      {/* Title — offset right of quote mark */}
      <div
        className="absolute font-extrabold"
        style={{
          top: '22%',
          left: '11%',
          right: '8%',
          fontSize: role === 'cover' ? '26px' : '22px',
          lineHeight: 1.1,
          color: palette.title,
          fontFamily: SERIF,
        }}
      >
        {title}
      </div>

      {/* Body */}
      {body && (
        <div
          className="absolute"
          style={{
            top: '54%',
            left: '11%',
            right: '8%',
            fontSize: '11px',
            lineHeight: 1.5,
            color: palette.body,
            fontFamily: SANS,
          }}
        >
          {body.slice(0, 200)}
        </div>
      )}

      {/* Closing quote bottom-right */}
      <span
        className="absolute select-none pointer-events-none font-bold leading-none"
        style={{
          right: '6%',
          bottom: '18%',
          fontSize: '70px',
          color: palette.accent,
          fontFamily: SERIF,
        }}
        aria-hidden
      >
        ”
      </span>

      {/* Attribution block bottom-left */}
      <div
        className="absolute flex items-center gap-2.5"
        style={{ bottom: '6%', left: '7%' }}
      >
        <div
          className="rounded-full overflow-hidden flex items-center justify-center font-extrabold"
          style={{
            width: '38px',
            height: '38px',
            backgroundColor: palette.accent,
            color: palette.bg,
            fontFamily: SERIF,
            fontSize: '18px',
          }}
        >
          {hasAvatar ? (
            <img
              src={img}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            (authorName || 'E').slice(0, 1).toUpperCase()
          )}
        </div>
        <div>
          <div
            className="font-bold"
            style={{ fontSize: '11px', color: palette.title, fontFamily: SERIF }}
          >
            {authorName || 'Anonymous'}
          </div>
          <div
            style={{
              fontSize: '8.5px',
              color: palette.kicker,
              fontFamily: MONO,
              letterSpacing: '0.08em',
            }}
          >
            {(brandLabel || '').toUpperCase()} · {fmtPage(idx, total)}
          </div>
        </div>
      </div>
    </LayoutFrame>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher — picks the right layout component by templateKey.
// Returns null if templateKey is not a custom layout (caller falls back to
// generic layout in CarouselDialog).
// ---------------------------------------------------------------------------
export function renderCustomLayoutSlide({ templateKey, ...props }) {
  switch (templateKey) {
    case 'editorial_bold':
      return <EditorialBoldSlide {...props} />;
    case 'index_card':
      return <IndexCardSlide {...props} />;
    case 'portrait_spotlight':
      return <PortraitSpotlightSlide {...props} />;
    case 'pop_frame':
      return <PopFrameSlide {...props} />;
    case 'magazine_mono':
      return <MagazineMonoSlide {...props} />;
    case 'quote_stack':
      return <QuoteStackSlide {...props} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Mini-tile renderer for the template gallery. Returns a tiny stylized
// preview that hints at each layout's signature shape (dot matrix, card,
// circle, etc.) so users can quickly identify them.
// ---------------------------------------------------------------------------
export function CustomLayoutMiniTile({ templateKey, cover }) {
  const c = cover; // { bg, title, body, kicker, accent }
  switch (templateKey) {
    case 'editorial_bold':
      return (
        <div
          className="aspect-[4/5] p-1.5 relative overflow-hidden"
          style={{ backgroundColor: c.bg }}
        >
          {/* Mini dot matrix */}
          <div className="absolute top-1 right-1 grid grid-cols-5 gap-px" aria-hidden>
            {Array.from({ length: 25 }).map((_, i) => (
              <span
                key={i}
                style={{
                  width: 2,
                  height: 2,
                  borderRadius: 99,
                  backgroundColor: c.accent,
                  opacity: 0.2,
                }}
              />
            ))}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 6, color: c.kicker }}>01 / 06</div>
          <div
            className="mt-2 font-bold leading-tight"
            style={{ fontFamily: SERIF, fontSize: 8, color: c.title }}
          >
            Bold serif<br />
            <span style={{ color: c.accent }}>headline.</span>
          </div>
        </div>
      );
    case 'index_card':
      return (
        <div
          className="aspect-[4/5] p-1 relative overflow-hidden"
          style={{ backgroundColor: c.bg }}
        >
          <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: c.accent }} />
          {/* Floating white card bottom-left */}
          <div
            className="absolute bg-white rounded-sm border border-gray-200 shadow-sm p-1.5"
            style={{ left: 4, bottom: 4, right: 12, top: 12 }}
          >
            <div
              className="font-extrabold leading-none"
              style={{ color: c.accent, fontSize: 16, fontFamily: SERIF }}
            >
              01
            </div>
            <div
              className="mt-1 font-bold"
              style={{ fontSize: 6, color: '#0A0A0A', fontFamily: SANS, lineHeight: 1.2 }}
            >
              Numbered insight card.
            </div>
          </div>
        </div>
      );
    case 'portrait_spotlight':
      return (
        <div
          className="aspect-[4/5] relative overflow-hidden"
          style={{ backgroundColor: c.bg }}
        >
          <div
            className="absolute top-0 right-0 h-full"
            style={{ width: '50%', backgroundColor: c.accent }}
          />
          <div
            className="absolute rounded-full"
            style={{
              right: 4,
              top: 4,
              width: 12,
              height: 12,
              backgroundColor: c.accent,
              border: `1px solid ${c.bg}`,
            }}
          />
          <div className="p-1.5">
            <div style={{ fontSize: 6, fontFamily: MONO, color: c.kicker }}>01 / 06</div>
            <div
              className="mt-1 font-bold leading-tight"
              style={{ fontFamily: SERIF, fontSize: 8, color: c.title }}
            >
              Spotlight on key insight
            </div>
          </div>
          <div
            className="absolute bottom-0 left-0 right-0"
            style={{ height: 6, backgroundColor: c.accent }}
          />
        </div>
      );
    case 'pop_frame':
      return (
        <div
          className="aspect-[4/5] relative overflow-hidden flex flex-col items-center"
          style={{ backgroundColor: c.bg }}
        >
          {/* Mini concentric circles */}
          <svg className="absolute -left-2 -top-2" width="28" height="28" viewBox="0 0 28 28" aria-hidden>
            {[12, 8, 4].map((r) => (
              <circle key={r} cx="14" cy="14" r={r} fill="none" stroke={c.accent} strokeWidth="0.8" />
            ))}
          </svg>
          <div
            className="mt-1.5 text-center font-extrabold uppercase"
            style={{ fontSize: 7, color: c.title, lineHeight: 1, fontFamily: SANS }}
          >
            BIG HEADLINE
          </div>
          <div
            className="mt-1.5 rounded-full"
            style={{
              width: '46%',
              aspectRatio: '1/1',
              backgroundColor: c.accent,
              boxShadow: `0 0 0 1.5px ${c.accent}`,
            }}
          />
        </div>
      );
    case 'magazine_mono':
      return (
        <div
          className="aspect-[4/5] relative overflow-hidden"
          style={{ backgroundColor: c.bg }}
        >
          <div className="absolute top-2 left-1.5 right-1.5 h-px" style={{ backgroundColor: c.title }} />
          <div className="absolute bottom-2 left-1.5 right-1.5 h-px" style={{ backgroundColor: c.title }} />
          <div
            className="absolute top-2.5 left-1.5 right-1.5 flex justify-between"
            style={{ fontSize: 5, fontFamily: MONO, color: c.kicker, letterSpacing: '0.1em' }}
          >
            <span>ISSUE 01</span>
            <span>PG 01/06</span>
          </div>
          <div
            className="absolute left-1.5 right-1.5 font-extrabold"
            style={{
              top: '36%',
              fontFamily: SANS,
              fontSize: 11,
              color: c.title,
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
            }}
          >
            Restraint.
          </div>
        </div>
      );
    case 'quote_stack':
      return (
        <div
          className="aspect-[4/5] relative overflow-hidden"
          style={{ backgroundColor: c.bg }}
        >
          <span
            className="absolute font-bold leading-none"
            style={{ left: 1, top: -6, fontSize: 36, color: c.accent, fontFamily: SERIF }}
            aria-hidden
          >
            “
          </span>
          <div
            className="absolute font-bold"
            style={{
              top: '38%',
              left: '20%',
              right: '8%',
              fontSize: 8,
              fontFamily: SERIF,
              color: c.title,
              lineHeight: 1.15,
            }}
          >
            Quotable insight worth reading.
          </div>
          <div
            className="absolute rounded-full"
            style={{
              left: 4,
              bottom: 4,
              width: 10,
              height: 10,
              backgroundColor: c.accent,
            }}
          />
        </div>
      );
    default:
      return null;
  }
}
